/* ============================================================
   Freshé dialogs: keyboard & screen-reader behaviour
   (Oct 16 2026, WCAG 2.1.2 / 2.4.3 / 4.1.2)

   Covers the two modal dialogs on the site that were announced
   as modal (role="dialog" aria-modal="true") but did not behave
   as modal for keyboard or screen-reader users:

   1. The newsletter pop-up (snippets/newsletter-modal.liquid,
      opened from global.js).
   2. The cart drawer (<cart-drawer>, assets/cart-drawer.js).

   Both are fixed the same way. A MutationObserver watches the
   element's own class attribute for its open state - .show_modal
   on the pop-up, .active on the drawer - so the fix fires no
   matter which code path opened it. That matters here:

   - global.js opens the pop-up from a setTimeout that captured
     __showModal at load, so wrapping that function later is
     bypassed.
   - cart-drawer.js only moves focus on 'transitionend', which
     does not always fire, and listens for Escape on the drawer
     itself, so Escape only works once focus is already inside.
     The two faults compound: focus never enters, so Escape never
     works.

   On open: remember what had focus, move focus into the dialog
   (retrying briefly, since .focus() is silently ignored while
   the window lacks system focus), keep Tab/Shift+Tab inside it,
   and pull focus straight back if it ever lands outside. Escape
   and backdrop click close it. On close: release everything and
   return focus to where it was.

   The cart drawer's own close() is reused so its state stays in
   sync. The pop-up's Escape/backdrop close also sets the
   suppression cookie, matching the "No thank you" button.

   Removing this file restores the previous behaviour exactly.
   ============================================================ */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:enabled, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, summary, [tabindex]:not([tabindex="-1"])';

  function visible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    return !!(r.width || r.height) && getComputedStyle(el).visibility !== 'hidden';
  }
  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), visible);
  }

  /* One controller per dialog. `spec` describes how to find it,
     how to tell it is open, where to put focus first, and how to
     ask it to close. */
  function Dialog(spec) {
    this.spec = spec;
    this.isOpen = false;
    this.opener = null;
    this.focusTimer = null;
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusLeak = this.onFocusLeak.bind(this);
    this.onBackdrop = this.onBackdrop.bind(this);
    this.onWindowFocus = this.onWindowFocus.bind(this);
  }

  Dialog.prototype.el = function () { return document.querySelector(this.spec.selector); };

  Dialog.prototype.open = function (m) {
    if (this.isOpen) return;
    this.isOpen = true;
    var a = document.activeElement;
    this.opener = a && a !== document.body && !m.contains(a) ? a : null;
    if (!m.hasAttribute('tabindex')) m.setAttribute('tabindex', '-1');
    this.ensureFocus(m);
    document.addEventListener('keydown', this.onKeydown, true);
    document.addEventListener('focusin', this.onFocusLeak, true);
    window.addEventListener('focus', this.onWindowFocus);
    if (this.spec.backdrop) m.addEventListener('click', this.onBackdrop);
  };

  Dialog.prototype.close = function (m) {
    if (!this.isOpen) return;
    this.isOpen = false;
    clearInterval(this.focusTimer);
    document.removeEventListener('keydown', this.onKeydown, true);
    document.removeEventListener('focusin', this.onFocusLeak, true);
    window.removeEventListener('focus', this.onWindowFocus);
    if (m) m.removeEventListener('click', this.onBackdrop);
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    if (this.opener && document.contains(this.opener)) {
      try { this.opener.focus(); } catch (e) {}
    }
    this.opener = null;
  };

  Dialog.prototype.firstTarget = function (m) {
    var pref = this.spec.initialFocus ? m.querySelector(this.spec.initialFocus) : null;
    if (pref && visible(pref)) return pref;
    var f = focusables(m);
    return f.length ? f[0] : m;
  };

  /* .focus() is silently ignored while the window lacks system
     focus (common right after navigation) and while the element
     is mid-transition. Retry briefly, and again on window focus. */
  Dialog.prototype.ensureFocus = function (m) {
    var self = this, attempts = 0;
    clearInterval(this.focusTimer);
    var tryFocus = function () {
      if (!self.isOpen) { clearInterval(self.focusTimer); return; }
      if (m.contains(document.activeElement)) { clearInterval(self.focusTimer); return; }
      var t = self.firstTarget(m);
      try { t.focus({ preventScroll: true }); } catch (e) { t.focus(); }
      if (++attempts >= 30) clearInterval(self.focusTimer);
    };
    this.focusTimer = setInterval(tryFocus, 100);
    tryFocus();
  };

  Dialog.prototype.onWindowFocus = function () {
    var m = this.el();
    if (this.isOpen && m && !m.contains(document.activeElement)) this.ensureFocus(m);
  };

  /* If focus lands outside the open dialog, wrap it back to the
     nearest edge: Tab off the end goes to the first control,
     Shift+Tab off the start goes to the last. */
  Dialog.prototype.onFocusLeak = function (e) {
    if (!this.isOpen) return;
    var m = this.el();
    if (!m || m.contains(e.target)) return;
    var f = focusables(m);
    if (!f.length) { m.focus(); return; }
    var after = m.compareDocumentPosition(e.target) & Node.DOCUMENT_POSITION_FOLLOWING;
    (after ? f[0] : f[f.length - 1]).focus();
  };

  Dialog.prototype.onKeydown = function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    if (!this.isOpen) return;
    // Let a nested disclosure (e.g. the cart note <details>) close first.
    var od = e.target && e.target.closest && e.target.closest('details[open]');
    if (od && this.el() && this.el().contains(od)) return;
    e.preventDefault();
    e.stopPropagation();
    this.spec.dismiss(this.el());
  };

  Dialog.prototype.onBackdrop = function (e) {
    var m = this.el();
    if (m && e.target === m) this.spec.dismiss(m);
  };

  Dialog.prototype.watch = function () {
    var self = this, m = this.el();
    if (!m) return false;
    var check = function () {
      var open = m.classList.contains(self.spec.openClass);
      if (open && !self.isOpen) self.open(m);
      else if (!open && self.isOpen) self.close(m);
    };
    new MutationObserver(check).observe(m, { attributes: true, attributeFilter: ['class'] });
    check();
    return true;
  };

  var dialogs = [
    new Dialog({
      selector: '#newsletter-modal',
      openClass: 'show_modal',
      initialFocus: '#newsletter-email',
      backdrop: true,
      dismiss: function (m) {
        if (typeof __setCookie === 'function') __setCookie('newsletter_shown3', 'true', 30);
        if (typeof __hideModal === 'function') __hideModal();
        else m.classList.remove('show_modal');
      }
    }),
    new Dialog({
      selector: 'cart-drawer',
      openClass: 'active',
      // The drawer's own close() clears .active and the body scroll lock.
      initialFocus: '.drawer__inner',
      backdrop: false, // the drawer has its own overlay element with a click handler
      dismiss: function (m) {
        if (m && typeof m.close === 'function') m.close();
        else if (m) m.classList.remove('active');
      }
    })
  ];

  function init() {
    var pending = dialogs.filter(function (d) { return !d.watch(); });
    if (!pending.length) return;
    // A dialog element that is not in the DOM yet (section render order).
    var tries = 0;
    var t = setInterval(function () {
      pending = pending.filter(function (d) { return !d.watch(); });
      if (!pending.length || ++tries > 50) clearInterval(t);
    }, 100);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
